<template>
    <div class="h-full flex flex-col">
        <div class="flex">
            <general-input
                v-model="searchText"
                label="Søk etter konto"
                placeholder="Kontonummer eller navn"
                data-cy="org-filter"
                class="grow mt-4"
                icon="i-ph-magnifying-glass"
                @update:model-value="applySearchText"
            />
        </div>
        <div class="h-full flex">
            <div class="h-full flex flex-col grow">
                <div class="flex my-2">
                    <div class="text-xl">Aktive kontoer</div>
                    <div class="mr-0 ml-auto items-center flex text-sm">
                        <span :class="!showSystemAccounts ? '' : 'text-base-content/50'">
                            Regnskapskontoer
                        </span>
                        <USwitch
                            v-model="showSystemAccounts"
                            size="xl"
                            class="mx-2"
                            autocomplete="off"
                        />
                        <span :class="showSystemAccounts ? '' : 'text-base-content/50'">
                            Systemkontoer
                        </span>
                    </div>
                </div>
                <ag-grid-vue
                    class="h-full grow"
                    :theme="agTheme"
                    :column-defs="columns"
                    :row-data="showSystemAccounts ? systemAccounts : bookkeepingAccountVisible"
                    :grid-options="activeAccountsGridOptions"
                    suppress-row-click-selection
                    data-cy="active-bookkeeping-account"
                />
            </div>
            <div class="flex flex-col justify-center items-center p-2 w-36">
                Flytt valgte
                <button class="btn btn-primary btn-xs m-2" :disabled="inactiveSelection.length === 0" @click="changeActiveStatus(true)">
                    <i class="i-ph-arrow-left-fill text-xl"/>
                </button>
                <button class="btn btn-primary btn-xs m-2" :disabled="activeSelection.length === 0" @click="changeActiveStatus(false)">
                    <i class="i-ph-arrow-right-fill text-xl"/>
                </button>
            </div>
            <div class="h-full flex flex-col grow">
                <div class="text-xl m-2">Inaktive kontoer</div>
                <ag-grid-vue
                    class="h-full grow"
                    :theme="agTheme"
                    :column-defs="columns"
                    :row-data="bookkeepingAccountInactive"
                    :grid-options="inactiveAccountsGridOptions"
                    suppress-row-click-selection
                    data-cy="inactive-bookkeeping-account"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {computed, inject, PropType, Ref, ref, shallowRef} from 'vue'
import AccountNoCell from '@/bookkeeping-account/AccountNoCell.vue'

import ActionCell from '@/bookkeeping-account/ActionCell.vue'
import {ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode, SelectionChangedEvent} from 'ag-grid-community'
import {fetchBookkeepingAccount, intervalSearch, updateMultipleAccountActiveStatus} from '@/bookkeeping-account/bookkeepingAccount.service'
import Fuse from 'fuse.js'
import {sortBy} from 'lodash'
import {agTheme} from '@/agUtils/ag-theme.service'
import GeneralInput from '@/components/GeneralInput.vue'
import VATCodeCell from '@/bookkeeping-account/VATCodeCell.vue'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {globalDefaultColDef} from '@/agUtils'

const activeAccountsGrid = shallowRef<GridApi>()
const inactiveAccountsGrid = shallowRef<GridApi>()
const activeSelection = ref<Array<string>>([])
const inactiveSelection = ref<Array<string>>([])
const showSystemAccounts = ref(false)
const panelId: Ref<number> | undefined = inject('panelId')

const searchText = ref('')

const props = defineProps({
        accounts: {
            type: Array as PropType<Array<BookkeepingAccount>>,
            required: true
        },
        showEditor: {
            type: Function,
            required: true
        }
    }
)

const changeActiveStatus = async (activate: boolean) => {
    const selection = activate ? inactiveSelection : activeSelection
    await updateMultipleAccountActiveStatus(selection.value, activate)
    await fetchBookkeepingAccount()
    activeAccountsGrid.value?.refreshCells()
    inactiveAccountsGrid.value?.refreshCells()
}

const systemAccounts = computed(() => {
    const systemAccountNumbers = ['27', '1500', '2400']
    return (props.accounts || [])
        .filter(account => systemAccountNumbers.some(numbers => account.accountNo.startsWith(numbers)))
})

const activeAccounts = computed(() => {
    const filteredAccounts = (props.accounts || []).filter(account => account.active)
    return sortBy(filteredAccounts, 'accountNo')
})

const inactiveAccounts = computed(() => {
    const filteredAccounts = (props.accounts || []).filter(account => !account.active)
    return sortBy(filteredAccounts, 'accountNo')
})

const bookkeepingAccountVisible = computed(() => {
    const filteredAccounts = (intervalSearch(activeAccounts.value, searchText.value) || activeAccounts.value)
        .filter(account => !systemAccounts.value.includes(account))
    return sortBy(filteredAccounts, 'accountNo')
})

const bookkeepingAccountInactive = computed(() => {
    const filteredAccounts = intervalSearch(inactiveAccounts.value, searchText.value) || inactiveAccounts.value
    return sortBy(filteredAccounts, 'accountNo')
})

const columns: ColDef[] = [
    {
        headerName: 'Konto',
        field: 'accountNo',
        width: 70,
        cellRenderer: AccountNoCell,
        contextMenuItems: []
    },
    {
        headerName: 'Kontonavn',
        field: 'name',
        flex: 1
    },
    {
        headerName: 'MVA',
        width: 60,
        cellRenderer: VATCodeCell,
        cellClass: 'text-center'
    },
    {
        headerName: '',
        cellRenderer: ActionCell,
        width: 40
    }
]

const filterRowsBySearch = (node: IRowNode) => {
    const account = node.data
    if (searchText.value === '')
        return true
    else if (account.accountNo.startsWith(searchText.value)) {
        return true
    }

    const fuse = new Fuse([
        account.name,
        account.description,
        ...(account.keywords || [])
    ], {includeScore: true, threshold: 0.3})

    return !!fuse.search(searchText.value).length
}


const applySearchText = () => {
    if (activeAccountsGrid.value) {
        activeAccountsGrid.value.onFilterChanged()
    }
}

const commonGridOptions: GridOptions = {
    defaultColDef: {
        ...globalDefaultColDef,
        resizable: true,
        sortable: true
    },
    animateRows: true,
    rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        isRowSelectable: (params: any) => {
            const rowData = params.data
            return !systemAccounts.value.includes(rowData)
        }
    },
    getRowId: (params: any) => params.data.accountNo,
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: filterRowsBySearch,
    suppressCellFocus: true,
    context: {
        showEditor: props.showEditor,
        panelId
    }
}

const activeAccountsGridOptions: GridOptions = {
    ...commonGridOptions,
    onGridReady: (event: GridReadyEvent) => {
        activeAccountsGrid.value = event.api
    },
    onSelectionChanged(event: SelectionChangedEvent) {
        const selectedRows = event.api.getSelectedRows()
        activeSelection.value = selectedRows.map(account => account.accountNo)
    }

}

const inactiveAccountsGridOptions: GridOptions = {
    ...commonGridOptions,
    onGridReady: (event: GridReadyEvent) => {
        inactiveAccountsGrid.value = event.api
    },
    onSelectionChanged(event: SelectionChangedEvent) {
        const selectedRows = event.api.getSelectedRows()
        inactiveSelection.value = selectedRows.map(account => account.accountNo)
    }
}

</script>

<style scoped>

.text-shadow {
    text-shadow: 0 0 1px black;
}

</style>